import { Draggable } from "@hello-pangea/dnd";
import { DragIndicator } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";
import { useState } from "react";
import { Exercise } from "@readyfit-common/models";
import { TrashIcon } from "src/grading/assets";
import { WorkoutFormValues } from "../EditWorkoutModal/EditWorkoutModal";
import useStyles from "./WorkoutForm.style";

interface ExerciseFieldsProps {
  index: number;
  exercise: Exercise;
  values: WorkoutFormValues;
  errors: FormikErrors<WorkoutFormValues>;
  handleChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
}

const ExerciseFields = ({
  index,
  exercise,
  values,
  errors,
  handleChange,
  setFieldValue,
}: ExerciseFieldsProps) => {
  const [isEditingName, setIsEditingName] = useState<number | null>(null);
  const classes = useStyles();
  const handleWorkoutExerciseChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    index: number,
  ) => {
    const value = event.target.value;
    setFieldValue(`exercises.${index}.${field}`, value);
  };

  const handleRemoveExercise = (index: number) => {
    const exercises = [...(values.exercises || [])];
    exercises.splice(index, 1);
    setFieldValue("exercises", exercises);
  };

  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    index: number,
  ) => {
    const value = event.target.value;
    // Only update if the value is a valid number or empty
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      handleWorkoutExerciseChange(event, field, index);
    }
  };

  return (
    <Draggable draggableId={index.toString()} index={index} key={index}>
      {(provided, snapshot) => {
        const style = {
          ...provided.draggableProps.style,
        };

        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={style}
          >
            <Grid container key={exercise.id} rowGap={1} spacing={1}>
              <Grid item xs={12}>
                <Grid container alignItems="center">
                  <div {...provided.dragHandleProps}>
                    <DragIndicator />
                  </div>
                  <Box sx={{ flexGrow: 1 }}>
                    {isEditingName === index ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        InputProps={{
                          disableUnderline: false,
                          style: { fontSize: 16, fontWeight: "bold" },
                        }}
                        autoFocus
                        value={values?.exercises?.[index].name}
                        onChange={(e) =>
                          handleChange(`exercises[${index}].name`)(
                            e as React.ChangeEvent<HTMLInputElement>,
                          )
                        }
                        onBlur={() => {
                          setIsEditingName(null);
                          setFieldValue(
                            `exercises[${index}].name`,
                            values?.exercises?.[index].name,
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setIsEditingName(null);
                            setFieldValue(
                              `exercises[${index}].name`,
                              values?.exercises?.[index].name,
                            );
                          }
                        }}
                      />
                    ) : (
                      <Typography
                        variant="h5"
                        fontSize={16}
                        onClick={() => {
                          setIsEditingName(index);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        {values?.exercises?.[index].name}
                      </Typography>
                    )}
                  </Box>

                  <IconButton onClick={() => handleRemoveExercise(index)}>
                    <TrashIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <InputLabel htmlFor="sets" className={classes.inputLabel}>
                  Sets
                </InputLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={values?.exercises?.[index].sets}
                  placeholder="Sets"
                  error={!!errors.exercises}
                  helperText={errors.exercises}
                  InputProps={{
                    className: classes.input,
                  }}
                  onChange={(e) => handleNumberChange(e, "sets", index)}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel htmlFor="reps" className={classes.inputLabel}>
                  Reps
                </InputLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={values?.exercises?.[index].reps}
                  placeholder="Reps"
                  error={!!errors.exercises}
                  helperText={errors.exercises}
                  InputProps={{
                    className: classes.input,
                  }}
                  onChange={(e) => handleNumberChange(e, "reps", index)}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel
                  htmlFor="description"
                  className={classes.inputLabel}
                >
                  Description
                </InputLabel>
                <TextField
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  value={values.exercises?.[index].description}
                  placeholder="Description"
                  error={!!errors.description}
                  helperText={errors.description}
                  InputProps={{
                    className: classes.input,
                  }}
                  onChange={(e) =>
                    handleWorkoutExerciseChange(e, "description", index)
                  }
                />
              </Grid>
            </Grid>
          </div>
        );
      }}
    </Draggable>
  );
};

export default ExerciseFields;
